import { CSSProperties, useState } from "react";
import { Crown, LockKeyhole, Sparkles } from "lucide-react";
import { Episode } from "../lib/types";

interface Props {
  episode?: Pick<Episode, "title" | "tier_required" | "effectiveTierRequired" | "accessBadgeLabel" | "series" | "public_thumbnail" | "member_thumbnail" | "poster_image" | "thumbnail_image" | "blurred_preview_image" | "series_cover_image" | "image_review_status" | "image_safety_status">;
  src?: string;
  title: string;
  subtitle?: string;
  tier?: string;
  unlocked?: boolean;
  variant?: "thumb" | "poster" | "cover";
  className?: string;
  fallbackSrc?: string;
  lockedFallbackSrc?: string;
  seed?: string;
  backdropSrc?: string;
}

const repeatedImportArt = /\/b9fec4cdb4bcc332\//i;
const safePublicPath = /^\/(?:patreon-assets|assets)\//i;

export function isRepeatedImportArt(src?: string) {
  return repeatedImportArt.test(String(src || ""));
}

function usableImage(src?: string, episode?: Props["episode"], explicitOverride = false) {
  if (!src || /placeholder\.svg$/i.test(src)) return false;
  if (!safePublicPath.test(src)) return false;
  if (!explicitOverride && repeatedImportArt.test(src) && episode?.image_review_status !== "approved" && episode?.image_safety_status !== "safe_public") return false;
  return true;
}

export function imageForEpisode(episode: Props["episode"], unlocked = false, variant: Props["variant"] = "thumb") {
  if (!episode) return "";
  if (!unlocked) {
    if (variant === "poster") return episode.poster_image || episode.thumbnail_image || episode.blurred_preview_image || episode.public_thumbnail;
    if (variant === "cover") return episode.series_cover_image || episode.poster_image || episode.thumbnail_image || episode.blurred_preview_image || episode.public_thumbnail;
    return episode.thumbnail_image || episode.blurred_preview_image || episode.public_thumbnail;
  }
  if (variant === "poster") return episode.poster_image || episode.thumbnail_image || episode.member_thumbnail;
  if (variant === "cover") return episode.series_cover_image || episode.poster_image || episode.thumbnail_image || episode.member_thumbnail;
  return episode.thumbnail_image || episode.poster_image || episode.member_thumbnail;
}

export function hasRealArtwork(src?: string) {
  return usableImage(src);
}

export function imageSourceType(episode?: Props["episode"], overridePath?: string) {
  if (overridePath && usableImage(overridePath, episode, true)) return "admin override";
  const image = imageForEpisode(episode, false, "thumb");
  if (usableImage(image, episode)) return "real Patreon asset";
  return "generated fallback";
}

function variantFor(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  return hash % 12;
}

function backdropStyle(src?: string) {
  if (!src || !/^\/(?:patreon-assets|thumbs|assets)\//.test(src)) return undefined;
  return { "--vault-backdrop": `url("${src.replaceAll("\"", "%22")}")` } as CSSProperties;
}

function safeSeriesLabel(value?: string) {
  if (/needs mapping review/i.test(value || "")) return "Vault Updates";
  return value || "";
}

export function VaultArtwork({ episode, src, title, subtitle, tier, unlocked, variant = "thumb", className = "", fallbackSrc, lockedFallbackSrc, seed, backdropSrc }: Props) {
  const image = src || imageForEpisode(episode, unlocked, variant) || (!unlocked ? lockedFallbackSrc : fallbackSrc) || fallbackSrc || "";
  const [failedImage, setFailedImage] = useState("");
  const motionClassName = `${className || "thumb"} safe-motion-art`.trim();
  if (usableImage(image, episode, Boolean(src)) && image !== failedImage) return <img src={image} alt="" className={motionClassName} loading="lazy" decoding="async" onError={() => setFailedImage(image)} />;
  const required = tier || episode?.effectiveTierRequired || episode?.tier_required || "paid";
  const requiredLabel = episode?.accessBadgeLabel || (required === "platinum" ? "Platinum" : required === "paid" ? "Members+" : "Free");
  const label = subtitle || safeSeriesLabel(episode?.series) || "YaoiGen Vault";
  const backdrop = backdropSrc || episode?.blurred_preview_image || episode?.thumbnail_image || image;
  return (
    <div className={`vault-art vault-art-${required} vault-art-variant-${variantFor(seed || title || label)} ${backdrop ? "vault-art-has-backdrop" : ""} safe-motion-art ${className}`} style={backdropStyle(backdrop)} aria-hidden="true">
      <div className="vault-art-sheen" />
      <div className="vault-art-top">
        <span className="brand-mark small-mark">YG</span>
        <span>{required === "platinum" ? <Crown size={15} /> : unlocked || required === "free" ? <Sparkles size={15} /> : <LockKeyhole size={15} />}{requiredLabel}</span>
      </div>
      <div className="vault-art-copy">
        <strong>{title}</strong>
        <em>{label}</em>
      </div>
    </div>
  );
}
